import {useSelector} from 'react-redux'
import {
  getAnonymousId,
  getDevicePlatform,
  getDeviceType,
  getIsVioFlutterApp,
  getLocale,
  getUrlParams,
  getUserCountryWithFallback
} from 'modules/meta/selectors'
import utmSources from 'utils/utmSources'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {isBrowser} from '@vio/core/src/_web/utils/environment'
import {getUserDeviceDetails} from '@vio/core/src/_web/utils/userAgent'
import {useAddFeatureFlagTracking} from '@vio/core/src/analytics/hooks/useAddFeatureFlagTracking'
import {
  selectHasVioEmail,
  selectIsAuthenticated,
  selectIsEmployee,
  selectIsMemberPlus
} from '@vio/core/src/auth/modules/selectors'
import {useOpticks} from '@vio/core/src/experiments/components/OpticksProvider'
import {useOpticksAudienceSegmentation} from '@vio/core/src/experiments/hooks/useOpticksAudienceSegmentation'
import {getForcedTogglesFromSearchQuery} from '@vio/core/src/experiments/utils/getForcedTogglesFromSearchQuery'
import {getMobileAppVersion} from '@vio/core/src/native'
import {UrlCheckoutParamsType} from '@vio/core/src/utils/url/types/UrlParams'
import {useDebugPanelToggles} from '@vio/debug-panel'

import type {AudienceSegmentationAttributes} from '@vio/core/src/experiments/types'
import type {ReactNode} from 'react'

const useSegmentationAttributes = (
  urlParams: UrlCheckoutParamsType
): AudienceSegmentationAttributes => {
  const {brandCode} = useBrand()
  const {utm_source, profile = ''} = urlParams

  return {
    application: 'checkout',
    env: process.env.NEXT_PUBLIC_ENV || 'production',
    brandCode,
    deviceCategory: useSelector(getDeviceType),
    devicePlatform: useSelector(getDevicePlatform),
    isWebView: useSelector(getIsVioFlutterApp),
    locale: useSelector(getLocale),
    trafficSource: utmSources(utm_source),
    userCountry: useSelector(getUserCountryWithFallback),
    profile
  }
}

const InitializeABTesting = ({children}: {children?: ReactNode}) => {
  const Opticks = useOpticks()
  const anonymousId = useSelector(getAnonymousId)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isEmployee = useSelector(selectIsEmployee)
  const hasVioEmail = useSelector(selectHasVioEmail)
  const isMemberPlus = useSelector(selectIsMemberPlus)
  const urlParams = useSelector(getUrlParams)

  const queryString = isBrowser ? location.search : ''

  const {applyLocalstorageToggles} = useDebugPanelToggles({
    params: queryString
  })

  const opticksAttributes = useSegmentationAttributes(urlParams)

  const audienceSegmentationAttributes = (): AudienceSegmentationAttributes => {
    // Some of the attributes below use client-side APIs, so we bail out on the server
    if (!isBrowser) {
      return {} as AudienceSegmentationAttributes
    }

    const {userAgentDetails} = getUserDeviceDetails()

    // Important: When adding a new segmentation attribute, double check if it can also be added on the server.
    // @file packages/checkout/src/utils/server/getExperimentSegmentationAttributes.ts
    return {
      ...opticksAttributes,
      isMetaTraffic: urlParams.isMetaTraffic ?? '',
      hasVioEmail,
      isAuthenticated,
      isMemberPlus,
      isEmployee,
      mobileAppVersion: getMobileAppVersion(),
      pathName: location.pathname,
      isBot: Boolean(userAgentDetails.isBot),
      userAgent: userAgentDetails.source
    }
  }

  useOpticksAudienceSegmentation(audienceSegmentationAttributes())

  useAddFeatureFlagTracking()

  // We do not support Opticks on the server, no need to initialize it
  if (isBrowser && anonymousId && Opticks) {
    Opticks.setUserId(anonymousId)
    Opticks.toggle('a-a-experiment')
    Opticks.setAudienceSegmentationAttributes(audienceSegmentationAttributes())

    const forcedTogglesFromQuery = getForcedTogglesFromSearchQuery(
      queryString
    ) as Record<string, string>

    const allForcedToggles = applyLocalstorageToggles({
      ...forcedTogglesFromQuery
    })

    if (allForcedToggles) {
      Opticks.forceToggles(allForcedToggles)
    }

    // Extra test to verify AB testing infrastructure is working as intended between Search / Checkout
    Opticks.toggle('a-a-experiment-v2')
  }

  return children as JSX.Element
}

export default InitializeABTesting
